import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import { useConfig } from "@/hooks/useConfig";
import PropertyCard from "@/components/PropertyCard";
import { Reveal } from "@/components/Reveal";

const Hero = ({ cfg, count }) => (
  <section className="relative overflow-hidden bg-[#1E3B2E] text-[#FDFBF7]" data-testid="destinations-hero">
    <img src="/images/ranthambore/1.jpg" alt="Ananta destinations" className="absolute inset-0 h-full w-full object-cover opacity-45" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#1E3B2E] via-[#1E3B2E]/70 to-transparent" />
    <div className="relative container-x py-20 sm:py-28 lg:py-32 max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="max-w-2xl">
        <p className="eyebrow text-[#C28B5E]">The Ananta Hotels · {count} destinations</p>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]" data-testid="destinations-title">{cfg.destinations_title || "From desert dunes to Goan shores"}</h1>
        <p className="mt-6 text-[15px] sm:text-base leading-8 text-[#FDFBF7]/85 font-light" data-testid="destinations-intro">{cfg.destinations_intro}</p>
        <Link to="/book" className="btn-gold mt-8" data-testid="destinations-book-btn">Book your stay</Link>
      </motion.div>
    </div>
  </section>
);

export default function Destinations() {
  useSeo("Destinations - Luxury Hotels & Resorts in India", "The Ananta Hotels destinations: Udaipur, Pushkar, Ranthambore, Bundi, Gir, Goa, Solan, Jaipur, Rajkot, Ranakpur, Ajabgarh, Katra, Jaunpur, Kota and Somnath.");
  const cfg = useConfig();
  const [list, setList] = useState([]);
  const [state, setState] = useState("All");
  useEffect(() => {
    api.get("/properties").then((r) => setList(r.data));
  }, []);
  const states = useMemo(() => ["All", ...new Set(list.map((p) => p.state))], [list]);
  const shown = state === "All" ? list : list.filter((p) => p.state === state);

  return (
    <>
      <Hero cfg={cfg} count={list.length || 15} />
      <section className="py-14 sm:py-20">
        <div className="container-x">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <p className="eyebrow text-[#C28B5E]">Choose your escape</p>
              <h2 className="mt-3 text-3xl sm:text-4xl text-[#1E3B2E] tracking-tight">Where will serenity find you?</h2>
            </div>
            <div className="-mx-5 px-5 sm:mx-0 sm:px-0 flex gap-2 overflow-x-auto lg:flex-wrap pb-1" data-testid="destinations-filters">
              {states.map((s) => (
                <button key={s} onClick={() => setState(s)} data-testid={`destinations-filter-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`shrink-0 h-9 px-4 text-[11px] uppercase tracking-[0.15em] border transition-colors ${state === s ? "bg-[#1E3B2E] text-white border-[#1E3B2E]" : "bg-white border-[#E2DCD0] hover:border-[#1E3B2E]"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="destinations-grid">
            {shown.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.06} className={`h-full [&>a]:h-full ${i === 0 && state === "All" ? "sm:col-span-2" : ""}`}>
                <PropertyCard p={p} tall={i < 2 && state === "All"} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
